import { doc, getDoc } from 'firebase/firestore';
import { ref, get } from 'firebase/database';
import { firestore, database } from '../../firebaseConfig';

/**
 * Resolves the live footer state right before sending an email.
 *
 * Why this exists
 * ───────────────
 * Footers are split across two backends:
 *
 *   - `world_users/{uid}/emailAccounts/{accountId}.footerId`   (Firestore)
 *   - `/World/{uid}/settings/footer/footerList`                (RTDB)
 *
 * `EmailAiPopup` keeps both in React state via separate live listeners
 * (`onSnapshot` for Firestore, `onValue` for RTDB). The send-click captures
 * those state values in a closure — if EITHER listener is even a few hundred
 * ms behind reality (e.g. user just saved the account → new footer
 * assignment, then immediately hit send), the cached state is stale and
 * `EmailFooterGenerator` silently falls back to "first active footer". Result:
 * wrong footer on the wire.
 *
 * This helper does one targeted live read per source, runs them in parallel,
 * and merges the result over the React-state fallback. It is intentionally
 * ZERO-FAIL: a read error never throws — the send always proceeds with the
 * fallback state (= identical to today's behavior). So worst case is "we
 * didn't gain extra safety on this one send", never "we broke sending".
 *
 * Cost / latency
 * ──────────────
 * Per call: 1× Firestore `getDoc` + 1× RTDB `get`, in parallel.
 * ≈ 150–250 ms wall-clock, all of it happens inside the existing
 * background `(async () => { ... })()` block in each mode, so the user-
 * facing UI doesn't wait on it.
 *
 * Defensive contract
 * ──────────────────
 *   - Missing inputs                  → return fallback unchanged
 *   - Account doc missing / no field  → keep fallback footerId
 *   - RTDB read fails                 → keep fallback footerSettings
 *   - ANY thrown error                → return fallback unchanged
 */

const FOOTER_KEYS = [
  'useMultipleFooters',
  'footerList',
  'footer_text',
  'footer_image_url',
  'footer_image_position',
  'footer_font',
  'footer_image_size',
  'socialIcons',
  'socialIconsPosition',
  'socialIconsMarginTop',
  'socialIconsMarginBottom',
  'optOutEnabled',
  'optOutText',
];

const mergeLiveFooterSettings = (fallback, live) => {
  const base = { ...(fallback || {}) };

  if (!live || typeof live !== 'object') {
    return base;
  }

  FOOTER_KEYS.forEach((key) => {
    if (key in live) {
      base[key] = live[key];
    }
  });

  base.useMultipleFooters = live.useMultipleFooters === true;
  if (!Array.isArray(base.footerList)) {
    base.footerList = [];
  }

  return base;
};

export const resolveFooterForSend = async ({
  userId,
  senderAccountId,
  fallbackFooterId = null,
  fallbackFooterSettings = {},
}) => {
  if (!userId || !senderAccountId) {
    return {
      footerId: fallbackFooterId,
      footerSettings: fallbackFooterSettings || {},
      source: 'fallback_missing_inputs',
    };
  }

  try {
    const accountRef = doc(firestore, 'world_users', userId, 'emailAccounts', senderAccountId);
    const footerSettingsRef = ref(database, `World/${userId}/settings/footer`);

    const [accountResult, footerResult] = await Promise.allSettled([
      getDoc(accountRef),
      get(footerSettingsRef),
    ]);

    let footerId = fallbackFooterId;
    let accountSource = 'fallback';
    if (accountResult.status === 'fulfilled' && accountResult.value && accountResult.value.exists()) {
      const data = accountResult.value.data() || {};
      if (Object.prototype.hasOwnProperty.call(data, 'footerId')) {
        footerId = data.footerId || null;
        accountSource = 'live';
      }
    } else if (accountResult.status === 'rejected') {
      console.warn('[resolveFooterForSend] account read failed, using fallback footerId:', accountResult.reason?.message || accountResult.reason);
    }

    let footerSettings = fallbackFooterSettings || {};
    let footerSource = 'fallback';
    if (footerResult.status === 'fulfilled' && footerResult.value) {
      const live = footerResult.value.val();
      if (live && typeof live === 'object') {
        footerSettings = mergeLiveFooterSettings(fallbackFooterSettings, live);
        footerSource = 'live';
      }
    } else if (footerResult.status === 'rejected') {
      console.warn('[resolveFooterForSend] footer settings read failed, using fallback:', footerResult.reason?.message || footerResult.reason);
    }

    return {
      footerId,
      footerSettings,
      source: `account:${accountSource}|settings:${footerSource}`,
    };
  } catch (err) {
    console.warn('[resolveFooterForSend] unexpected error, using fallback:', err?.message || err);
    return {
      footerId: fallbackFooterId,
      footerSettings: fallbackFooterSettings || {},
      source: 'fallback_exception',
    };
  }
};

export default resolveFooterForSend;
